import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { debounce } from "lodash";
import { fetchPredefinedWHLocs } from "../api/LocationsApi";
import { parseAddressComponents } from "../helpers/common";
import Utils from "../utils/Utils";
import { WarehouseContext } from "../context/WarehouseContext";
import { OrgContext } from "../context/OrgContext";

/**
 * Single source of address-autocomplete logic for the whole app.
 *
 * Used by:
 *   - stories/components/SearchAddress_v2.jsx — antd-styled, v1/v2 callers
 *   - components/common/linchpin/LpAddressSearch.jsx — Linchpin-styled, v3 callers
 *
 * Returns: { searchText, setSearchText, results, loading, onSearch, reset,
 *            resolveSelection, fetchDetails }
 *
 * COST NOTE (Google Places billing): Place Details is the expensive SKU. By
 * default we DO NOT call it per suggestion — the dropdown is rendered from the
 * Autocomplete predictions alone (which already carry display text), and Place
 * Details is fetched only for the ONE row the user selects (via
 * `resolveSelection`). Orgs that need the richer/more-accurate formatted address
 * on every suggestion can opt back into the legacy per-suggestion details by
 * setting the org config `show_detailed_address_suggestions = "true"`.
 *
 * Additional guards (both modes): min 3 chars before hitting Places, 600ms
 * debounce, and at most MAX_WEB_RESULTS predictions.
 */

// Min characters before we hit the (billed) Places Autocomplete. Predefined
// (our own warehouse-location API) is unaffected and still searches from char 1.
const MIN_WEB_CHARS = 3;
// Cap on web predictions shown (and, in detailed mode, details fetched).
const MAX_WEB_RESULTS = 5;
const DETAIL_FIELDS = ["name", "formatted_address", "geometry", "address_components", "types", "place_id"];

// Parse a Google PlacesService `getDetails` result into the app's web-result
// shape (with parsed l_address + coordinates). Returns null on a bad/empty place
// so callers can filter it out. Pure — shared by the eager path and on-select.
function buildWebItem(google, place, status) {
  if (status !== google?.maps?.places?.PlacesServiceStatus?.OK || !place?.geometry?.location) {
    return null;
  }
  // location.lat/lng are functions on a live LatLng but plain numbers on some
  // serialized results — handle both so we never call a non-function.
  const loc = place.geometry.location;
  const lat = typeof loc.lat === "function" ? loc.lat() : loc.lat;
  const lng = typeof loc.lng === "function" ? loc.lng() : loc.lng;
  if (!lat || !lng) return null;
  // getDetails may omit address_components for some place types; default to []
  // so parseAddressComponents (a for..of) never throws on undefined.
  const { l_address } = parseAddressComponents(place.address_components || []);
  const isEstablishment = (place.types || []).includes("establishment");
  return {
    id: place.place_id,
    place_id: place.place_id,
    name: place.name,
    company_name: isEstablishment ? place.name : "",
    address: place.formatted_address,
    l_address: { ...l_address, coordinates: [lng, lat] },
    type: "web",
  };
}

export default function useAddressAutocomplete(opts = {}) {
  const {
    hidePredefined = false,
    hideWebResults = false,
    orderTypeConfig = {},
    selectedWarehouseId = "",
    linehaul_code,
    linehaul_label,
    // Restrict predefined suggestions to warehouses only (l_type 'WH'). Used by the
    // standalone-linehaul quote destination, which must be a warehouse terminal.
    warehousesOnly = false,
  } = opts;

  const warehouseContext = useContext(WarehouseContext) || {};
  const orgContext = useContext(OrgContext) || {};
  // Legacy per-suggestion Place Details, opt-in per org. Anything other than the
  // literal "true" (including unset) keeps the cost-optimized details-on-select flow.
  const detailedSuggestions = orgContext?.orgSettings?.show_detailed_address_suggestions === "true";

  const [searchText, setSearchText] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const sessionTokenRef = useRef(null);

  useEffect(() => { Utils.loadGoogleMaps(); }, []);

  const fetchPredefined = useCallback(async (text) => {
    const isLH = ["LH", "LHD", "NLH", "PLH", "PLHD"].includes(orderTypeConfig.code);
    const warehouseId = typeof selectedWarehouseId === "object" ? (selectedWarehouseId && selectedWarehouseId.id) : (selectedWarehouseId || "");
    const selectedWh = (warehouseContext.warehouses || []).find((e) => e.id == warehouseId);
    const sendWhId = (selectedWh && selectedWh.show_only_predefined_locations) || false;
    const whId = sendWhId && !isLH ? warehouseId : "";
    try {
      const data = await fetchPredefinedWHLocs({ search_text: text, warehouse_id: whId });
      const filtered = (data.locations || []).filter((stop) =>
        (warehousesOnly || (linehaul_code && linehaul_label)) ? stop.l_type === "WH" : true
      );
      return filtered.map((stop) => ({ ...stop, type: "predefined" }));
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("Error fetching predefined stops:", e);
      return [];
    }
  }, [orderTypeConfig.code, selectedWarehouseId, linehaul_code, linehaul_label, warehousesOnly, warehouseContext.warehouses]);

  // Autocomplete predictions -> web results. Default (cheap) mode returns the
  // predictions themselves (no Place Details); detailed mode fetches Place
  // Details per prediction (legacy behavior, opt-in via org config).
  const fetchWeb = useCallback(async (text, token, detailed) => {
    if (!window.google?.maps?.places) return [];
    if ((text || "").trim().length < MIN_WEB_CHARS) return []; // don't bill Places on 1-2 chars
    const google = window.google;
    try {
      const auto = new google.maps.places.AutocompleteService();
      const predictions = await new Promise((res) => auto.getPlacePredictions({ input: text, sessionToken: token }, (p) => res(p)));
      if (!Array.isArray(predictions)) return [];
      const capped = predictions.slice(0, MAX_WEB_RESULTS);

      if (!detailed) {
        // Cost-optimized default: no per-suggestion Place Details. The prediction's
        // own text drives the dropdown; l_address/coordinates are filled on select
        // via resolveSelection().
        return capped
          .filter((pred) => pred?.place_id)
          .map((pred) => {
            const main = pred.structured_formatting?.main_text || pred.description || "";
            const isEstablishment = (pred.types || []).includes("establishment");
            return {
              id: pred.place_id,
              place_id: pred.place_id,
              name: main,
              company_name: isEstablishment ? main : "",
              address: pred.description || "",
              l_address: null,      // filled on select
              type: "web",
              _needsDetails: true,  // signals resolveSelection to fetch details
            };
          });
      }

      // Legacy detailed mode: Place Details for each (capped) prediction.
      const ps = new google.maps.places.PlacesService(document.createElement("div"));
      const details = await Promise.all(
        capped
          .filter((pred) => pred?.place_id)
          .map((pred) => new Promise((res) => {
            ps.getDetails({ placeId: pred.place_id, fields: DETAIL_FIELDS, sessionToken: token }, (place, status) => {
              res(buildWebItem(google, place, status));
            });
          }))
      );
      return details.filter(Boolean);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("Error fetching web address results:", e);
      return [];
    }
  }, []);

  // Fetch Place Details for a single place (used on selection in the cheap flow).
  const fetchDetails = useCallback((placeId) => new Promise((res) => {
    if (!placeId || !window.google?.maps?.places) return res(null);
    const google = window.google;
    try {
      const ps = new google.maps.places.PlacesService(document.createElement("div"));
      ps.getDetails({ placeId, fields: DETAIL_FIELDS, sessionToken: sessionTokenRef.current }, (place, status) => {
        res(buildWebItem(google, place, status));
      });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("Error fetching place details:", e);
      res(null);
    }
  }), []);

  // Resolve the user's chosen row into a fully-populated item (with l_address +
  // coordinates). Fetches Place Details only when the row is a details-deferred
  // web result. Renews the session token after every committed selection (a
  // Places session ends once its details are fetched).
  // Returns null when a web row's details can't be resolved, so callers can
  // surface an error instead of committing an address with no l_address.
  const resolveSelection = useCallback(async (item) => {
    if (!item) return null;
    if (item.type === "web" && item._needsDetails) {
      const d = await fetchDetails(item.place_id || item.id);
      sessionTokenRef.current = null;
      return d ? { ...item, ...d, _needsDetails: false } : null;
    }
    sessionTokenRef.current = null;
    return item;
  }, [fetchDetails]);

  // Latest props/handlers kept in a ref so the debounced fn is built only once.
  const latestRef = useRef({});
  latestRef.current = { hidePredefined, hideWebResults, fetchPredefined, fetchWeb, detailedSuggestions };

  // Monotonic request id + mounted flag: several fetches can be in flight at once
  // (typing across debounce windows). Only the latest request may commit results,
  // so a slow earlier response can't overwrite a newer one, and nothing sets state
  // after unmount.
  const reqSeqRef = useRef(0);
  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);

  const debouncedFetch = useMemo(
    () => debounce(async (text, token) => {
      const seq = ++reqSeqRef.current;
      setLoading(true);
      const ctx = latestRef.current;
      try {
        const [pre, web] = await Promise.all([
          !ctx.hidePredefined ? ctx.fetchPredefined(text) : [],
          !ctx.hideWebResults ? ctx.fetchWeb(text, token, ctx.detailedSuggestions) : [],
        ]);
        if (seq !== reqSeqRef.current || !mountedRef.current) return; // stale / unmounted
        setResults([...(pre || []), ...(web || [])]);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error("Address autocomplete failed:", e);
        if (seq === reqSeqRef.current && mountedRef.current) setResults([]);
      } finally {
        if (seq === reqSeqRef.current && mountedRef.current) setLoading(false);
      }
    }, 600),
    []
  );
  useEffect(() => () => debouncedFetch.cancel(), [debouncedFetch]);

  const onSearch = useCallback((value) => {
    setSearchText(value);
    if (!value || value.length === 0) {
      // Invalidate any in-flight/queued fetch so a late response can't repopulate
      // the just-cleared dropdown.
      reqSeqRef.current++;
      debouncedFetch.cancel();
      setResults([]);
      setLoading(false);
      return;
    }
    if (!window.google?.maps?.places) return;
    if (!sessionTokenRef.current) {
      sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
    }
    debouncedFetch(value, sessionTokenRef.current);
  }, [debouncedFetch]);

  const reset = useCallback(() => {
    reqSeqRef.current++;      // invalidate any in-flight fetch
    debouncedFetch.cancel();  // drop any queued (not-yet-fired) fetch
    setSearchText("");
    setResults([]);
    setLoading(false);
    sessionTokenRef.current = null;
  }, [debouncedFetch]);

  return { searchText, setSearchText, results, loading, onSearch, reset, resolveSelection, fetchDetails };
}
